import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/hooks/use-i18n";
import { HeaderBar } from "@/components/HeaderBar";
import { Hero } from "@/components/Hero";
import { PillarCards } from "@/components/PillarCards";
import { ServiceCards } from "@/components/ServiceCards";
import { StepsTimeline } from "@/components/StepsTimeline";
import { TrustSection } from "@/components/TrustSection";
import { Testimonials } from "@/components/Testimonials";
import { FAQ } from "@/components/FAQ";
import { ContactCTA } from "@/components/ContactCTA";
import { Footer } from "@/components/Footer";
import { SectionDivider } from "@/components/ui/SectionDivider";
import { ScrollReveal } from "@/components/ui/ScrollReveal";
import { useAdaptiveLayout } from "@/hooks/useAdaptiveLayout";
import { trackLayoutVariantShown } from "@/lib/uxTracking";

// "trust" is inserted before "faq" in every variant — adaptive ordering preserved.
const SECTION_ORDER: Record<"default" | "trust_first" | "action_first" | "price_first", string[]> = {
  default:      ["hero", "pillars", "certifications", "services", "steps", "trust", "testimonials", "faq", "contact"],
  trust_first:  ["hero", "pillars", "certifications", "steps", "services", "trust", "testimonials", "faq", "contact"],
  action_first: ["hero", "services", "pillars", "certifications", "steps", "trust", "testimonials", "faq", "contact"],
  price_first:  ["hero", "services", "steps", "pillars", "certifications", "trust", "testimonials", "faq", "contact"],
};

export default function Home() {
  const { t, dir, lang, setLang } = useI18n();
  const layout = useAdaptiveLayout();
  const homeVariant = layout.homeVariant ?? "default";
  const order = SECTION_ORDER[homeVariant] ?? SECTION_ORDER.default;

  useEffect(() => {
    document.title = `${t("home.metaTitle")} — Aurelian Private Care`;
  }, [t, lang]);

  useEffect(() => {
    if (layout.isAdaptive && homeVariant) {
      trackLayoutVariantShown(homeVariant);
    }
  }, [layout.isAdaptive, homeVariant]);

  const sections: { key: string; el: React.ReactNode }[] = [
    { key: "hero",         el: <Hero t={t} dir={dir} /> },
    { key: "pillars",      el: <ScrollReveal><PillarCards t={t} /></ScrollReveal> },
    {
      key: "certifications",
      el: (
        <ScrollReveal>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-wrap items-center justify-center gap-8 mt-12 opacity-50 hover:opacity-100 transition-opacity duration-500"
          >
            <img src="/certifications/jci.svg" alt="JCI Accredited" className="h-10 grayscale hover:grayscale-0 transition-all duration-300" />
            <img src="/certifications/iso.svg" alt="ISO 9001 Certified" className="h-10 grayscale hover:grayscale-0 transition-all duration-300" />
            <img src="/certifications/turkiye-health.svg" alt="Türkiye Health Tourism" className="h-10 grayscale hover:grayscale-0 transition-all duration-300" />
          </motion.div>
        </ScrollReveal>
      ),
    },
    { key: "services",     el: <ScrollReveal delay={0.1}><ServiceCards t={t} /></ScrollReveal> },
    { key: "steps",        el: <ScrollReveal delay={0.1}><StepsTimeline t={t} dir={dir} /></ScrollReveal> },
    { key: "trust",        el: <ScrollReveal><TrustSection t={t} /></ScrollReveal> },
    { key: "testimonials", el: <ScrollReveal direction="up"><Testimonials t={t} /></ScrollReveal> },
    { key: "faq",          el: <ScrollReveal><FAQ t={t} /></ScrollReveal> },
    { key: "contact",      el: <ScrollReveal direction="up"><ContactCTA t={t} /></ScrollReveal> },
  ];

  const ordered = order
    .map((k) => sections.find((s) => s.key === k))
    .filter(Boolean) as { key: string; el: React.ReactNode }[];

  return (
    <div className="flex flex-col min-h-screen" dir={dir}>
      <HeaderBar lang={lang} onChangeLang={setLang} dir={dir} />
      <main className="flex-1" data-ux-home-variant={homeVariant}>
        {ordered.map((s, i) => (
          <React.Fragment key={s.key}>
            {s.el}
            {i < ordered.length - 1 && s.key !== "hero" && <SectionDivider />}
          </React.Fragment>
        ))}
      </main>
      <Footer t={t} dir={dir} />
    </div>
  );
}
